/**
 * =====================================================================================
 * SERVICIO DE SUCURSALES - sucursales.service.js
 * =====================================================================================
 *
 * Este servicio gestiona las SUCURSALES del emisor.
 * Una sucursal es un establecimiento físico desde donde se emiten facturas.
 *
 * ¿QUÉ ES UNA SUCURSAL?
 * ---------------------
 * En el contexto de facturación electrónica, una sucursal (o establecimiento)
 * es cada punto de venta o ubicación física de la empresa desde donde se factura.
 *
 * EJEMPLOS:
 * - Casa matriz (oficinas centrales)
 * - Sucursal zona norte
 * - Sucursal centro comercial X
 * - Bodega (si factura directamente)
 *
 * RELACIÓN EMISOR ↔ SUCURSALES:
 * -----------------------------
 *
 * EMISOR (empresa)
 *    │
 *    ├── SUCURSAL 1: Casa matriz (M001P001)
 *    │      └── Emite facturas con código M001P001
 *    │
 *    ├── SUCURSAL 2: Sucursal norte (S001P001)
 *    │      └── Emite facturas con código S001P001
 *    │
 *    └── SUCURSAL 3: Sucursal sur (S002P001)
 *           └── Emite facturas con código S002P001
 *
 * DATOS DE UNA SUCURSAL:
 * ----------------------
 * - Nombre: "Casa Matriz", "Sucursal Norte"
 * - Código de Establecimiento: Identificador único (ej: M001P001)
 *   Este código aparece en el NÚMERO DE CONTROL de cada factura:
 *   DTE-01-[M001P001]-000000000000001
 *         ↑ aquí
 * - Dirección: Ubicación física
 * - Teléfono
 * - Tipo de Establecimiento: Casa matriz, sucursal, bodega, etc.
 *
 * IMPORTANCIA EN LA FACTURACIÓN:
 * ------------------------------
 * 1. Cada factura se emite desde UNA sucursal específica
 * 2. El código de sucursal forma parte del número de control
 * 3. Hacienda identifica las facturas por establecimiento
 * 4. Permite control de ventas por ubicación
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/views/configuracion/SucursalesView.vue: CRUD de sucursales
 * - src/views/emision/FacturaView.vue: Selecciona sucursal al emitir
 * - src/services/facturas.service.js: Usa código de sucursal para generar DTE
 * - Backend: Controllers/SucursalesController.cs
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'
import authService from './auth.service'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE SUCURSALES
 * =====================================================================================
 *
 * Proporciona operaciones CRUD para sucursales.
 * Las sucursales están asociadas al emisor del usuario actual,
 * por lo que el backend filtra automáticamente por emisorId.
 */
const sucursalesService = {
  /**
   * ===========================================================================
   * MÉTODO: getAll
   * ===========================================================================
   *
   * Obtiene todas las sucursales del emisor actual.
   *
   * ENDPOINT: GET /api/sucursales
   *
   * NOTA: El backend obtiene el emisorId del token JWT del usuario
   * y retorna solo las sucursales de ese emisor.
   *
   * @returns {Promise<Array>} Lista de sucursales
   *
   * EJEMPLO DE RESPUESTA:
   * [
   *   {
   *     id: 1,
   *     nombre: "Casa Matriz",
   *     codigo: "M001P001",
   *     direccion: "Calle Principal #123",
   *     telefono: "2222-3333",
   *     tipoEstablecimiento: { id: 2, codigo: "02", valor: "Casa matriz" },
   *     activo: true
   *   },
   *   {
   *     id: 2,
   *     nombre: "Sucursal Norte",
   *     codigo: "S001P001",
   *     ...
   *   }
   * ]
   */
  async getAll(options = {}) {
    const params = {}
    if (options.soloActivos) params.soloActivos = true
    if (options.soloMiSucursal) params.soloMiSucursal = true
    const response = await api.get('/sucursales', { params })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getByEmisorId (Solo SuperAdmin)
   * ===========================================================================
   *
   * Obtiene todas las sucursales de un emisor específico.
   * Este método es para SuperAdmin que edita cualquier emisor.
   *
   * ESTRATEGIA DE FALLBACK:
   * -----------------------
   * 1. Primero llama a /api/auth/cambiar-contexto para cambiar el contexto
   *    del token JWT al emisor seleccionado
   * 2. Actualiza el token en localStorage
   * 3. Luego llama a /api/sucursales que ahora devuelve las sucursales
   *    del emisor en el nuevo contexto
   *
   * NOTA: El endpoint /api/sucursales/by-emisor/{emisorId} no existe actualmente
   * en el backend. Esta solución usa cambiar-contexto como workaround.
   *
   * @param {number} emisorId - ID del emisor
   * @returns {Promise<Array>} Lista de sucursales del emisor
   */
  async getByEmisorId(emisorId) {
    try {
      // Paso 1: Cambiar contexto al emisor seleccionado
      // Esto genera un nuevo token JWT con el emisor en el contexto
      const contextoResponse = await authService.cambiarContexto(emisorId)

      // Paso 2: Actualizar el token en localStorage para que las siguientes
      // peticiones usen el nuevo contexto
      if (contextoResponse.token) {
        localStorage.setItem('dte_token', contextoResponse.token)
      }

      // Paso 3: Ahora GET /api/sucursales devolverá las sucursales del emisor
      // porque el backend obtiene el emisorId del nuevo token
      const response = await api.get('/sucursales')
      return response.data
    } catch (error) {
      // Manejar errores específicos
      if (error.response?.status === 403) {
        // 403 = El endpoint /api/sucursales no permite SuperAdmin con el token de cambiar-contexto
        // Esto es una limitación conocida del backend: el endpoint cambiar-contexto
        // no incluye ClaimTypes.Role en el token, solo "RolNombre"
        console.warn('=== LIMITACIÓN DEL BACKEND ===')
        console.warn('El endpoint /api/sucursales requiere roles específicos (EmisorAdmin, GerenteSucursal, Cajero)')
        console.warn('El token generado por cambiar-contexto no tiene el claim ClaimTypes.Role requerido')
        console.warn('Solución recomendada: Modificar AuthController.CambiarContexto para incluir ClaimTypes.Role')
      } else if (error.response?.status === 404) {
        console.warn('El endpoint no fue encontrado en el backend')
      }
      console.error('Error en getByEmisorId:', error)
      // Si hay error, retornar array vacío para no romper la UI
      return []
    }
  },

  /**
   * ===========================================================================
   * MÉTODO: getById
   * ===========================================================================
   *
   * Obtiene una sucursal específica por su ID.
   *
   * ENDPOINT: GET /api/sucursales/{id}
   *
   * @param {number} id - ID de la sucursal
   * @returns {Promise<Object>} Datos completos de la sucursal
   */
  async getById(id) {
    const response = await api.get(`/sucursales/${id}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: create
   * ===========================================================================
   *
   * Crea una nueva sucursal.
   *
   * ENDPOINT: POST /api/sucursales
   *
   * El backend asocia automáticamente la sucursal al emisor del usuario.
   *
   * VALIDACIONES:
   * - El código de establecimiento debe ser único dentro del emisor
   * - Debe tener 8 caracteres alfanuméricos
   *
   * @param {Object} sucursalData - Datos de la sucursal
   * @param {string} sucursalData.nombre - Nombre descriptivo
   * @param {string} sucursalData.codigo - Código de establecimiento (8 chars)
   * @param {string} sucursalData.direccion - Dirección física
   * @param {string} sucursalData.telefono - Teléfono de contacto
   * @param {number} sucursalData.tipoEstablecimientoId - ID del tipo (catálogo)
   * @returns {Promise<Object>} Sucursal creada con ID asignado
   */
  async create(sucursalData) {
    // Transformar a PascalCase para el backend .NET
    // Campos según CreateSucursalDto del backend:
    // - Codigo (requerido, max 20): Identificador único por emisor
    // - Nombre (requerido, 3-200): Nombre descriptivo
    // - Direccion, Telefono, CorreoElectronico: Opcionales
    // - CatDepartamentoId, CatMunicipioId, CatTipoEstablecimientoId: FKs a catálogos
    // - CodigoEstablecimiento, CodigoPuntoVentaMH, CodigoPuntoVenta: Códigos MH
    const dto = {
      Codigo: sucursalData.codigo || null,
      Nombre: sucursalData.nombre,
      Direccion: sucursalData.direccion || sucursalData.direccionComplemento,
      Telefono: sucursalData.telefono,
      CorreoElectronico: sucursalData.email || sucursalData.correoElectronico,
      CatDepartamentoId: parseInt(sucursalData.catDepartamentoId || sucursalData.departamento) || null,
      CatMunicipioId: parseInt(sucursalData.catMunicipioId || sucursalData.municipio) || null,
      CatTipoEstablecimientoId: parseInt(sucursalData.catTipoEstablecimientoId || sucursalData.tipoEstablecimiento) || null,
      // Código MH - usar codigoEstablecimientoMH (legacy) o codigoEstablecimiento
      CodigoEstablecimiento: sucursalData.codigoEstablecimientoMH || sucursalData.codigoEstablecimiento,
      CodigoPuntoVentaMH: sucursalData.codigoPuntoVentaMH,
      CodigoPuntoVenta: sucursalData.codigoPuntoVenta,
      // Campos de responsable de contingencia
      ContingenciaNombreResponsable: sucursalData.contingenciaNombreResponsable || null,
      ContingenciaTipoDocResponsable: sucursalData.contingenciaTipoDocResponsable || null,
      ContingenciaNumeroDocResponsable: sucursalData.contingenciaNumeroDocResponsable || null
    }
    const response = await api.post('/sucursales', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: update
   * ===========================================================================
   *
   * Actualiza una sucursal existente.
   *
   * ENDPOINT: PUT /api/sucursales/{id}
   *
   * @param {number} id - ID de la sucursal
   * @param {Object} sucursalData - Datos actualizados de la sucursal
   * @returns {Promise<Object>} Sucursal actualizada
   */
  async update(id, sucursalData) {
    // Transformar a PascalCase para el backend .NET
    // Campos según UpdateSucursalDto del backend
    const dto = {
      Codigo: sucursalData.codigo || null,
      Nombre: sucursalData.nombre,
      Direccion: sucursalData.direccion || sucursalData.direccionComplemento,
      Telefono: sucursalData.telefono,
      CorreoElectronico: sucursalData.email || sucursalData.correoElectronico,
      CatDepartamentoId: parseInt(sucursalData.catDepartamentoId || sucursalData.departamento) || null,
      CatMunicipioId: parseInt(sucursalData.catMunicipioId || sucursalData.municipio) || null,
      CatTipoEstablecimientoId: parseInt(sucursalData.catTipoEstablecimientoId || sucursalData.tipoEstablecimiento) || null,
      // Código MH - usar codigoEstablecimientoMH (legacy) o codigoEstablecimiento
      CodigoEstablecimiento: sucursalData.codigoEstablecimientoMH || sucursalData.codigoEstablecimiento,
      CodigoPuntoVentaMH: sucursalData.codigoPuntoVentaMH,
      CodigoPuntoVenta: sucursalData.codigoPuntoVenta,
      // Campos de responsable de contingencia
      ContingenciaNombreResponsable: sucursalData.contingenciaNombreResponsable || null,
      ContingenciaTipoDocResponsable: sucursalData.contingenciaTipoDocResponsable || null,
      ContingenciaNumeroDocResponsable: sucursalData.contingenciaNumeroDocResponsable || null
    }
    const response = await api.put(`/sucursales/${id}`, dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: toggleActive
   * ===========================================================================
   *
   * Activa o desactiva una sucursal.
   *
   * ENDPOINT: PATCH /api/sucursales/{id}/toggle-active
   *
   * ¿PARA QUÉ DESACTIVAR?
   * ---------------------
   * - Sucursal cerrada temporalmente
   * - Punto de venta que ya no opera
   * - NO eliminar porque puede haber facturas históricas
   *
   * Una sucursal desactivada:
   * - NO aparece en el selector al crear factura
   * - SÍ aparece en el historial de facturas donde fue usada
   *
   * @param {number} id - ID de la sucursal
   * @returns {Promise<Object>} { id, activo: boolean }
   */
  async toggleActive(id) {
    const response = await api.patch(`/sucursales/${id}/toggle-active`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getAllActivas
   * ===========================================================================
   *
   * Obtiene TODAS las sucursales activas del emisor sin restricción de rol.
   * Usado en vistas donde todos los roles necesitan ver todas las sucursales
   * (ej: stock).
   *
   * ENDPOINT: GET /api/sucursales/todas-activas
   *
   * @returns {Promise<Array>} Lista de sucursales activas
   */
  async getAllActivas() {
    const response = await api.get('/sucursales/todas-activas')
    return response.data
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import sucursalesService from '@/services/sucursales.service'
 *
 * // Obtener todas las sucursales
 * const sucursales = await sucursalesService.getAll()
 *
 * // Crear nueva sucursal
 * await sucursalesService.create({
 *   nombre: 'Sucursal Centro',
 *   codigo: 'S003P001',
 *   direccion: 'Avenida Central #456',
 *   telefono: '2222-4444',
 *   tipoEstablecimientoId: 1  // Sucursal/Agencia
 * })
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/views/configuracion/SucursalesView.vue → CRUD de sucursales
 * - src/views/emision/FacturaView.vue → Selector de sucursal al emitir
 * - src/store/modules/emisor.js → Carga sucursales del emisor
 * - src/components/emision/DatosGeneralesFactura.vue → Muestra sucursal actual
 */
export default sucursalesService
